import type {FC} from 'react';

import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';
import {cn} from '@/modules/foundation/shared/tailwind';

import {Checkbox} from '../form/Checkbox';
import {RadioTileFrame} from '../form/RadioTileFrame';
import {FormattedItemPrice} from '../item/FormattedItemPrice';

import {type EmbroideryConfiguration} from './configuration';
import type {EmbroideryOptionCoreFragment} from './query';

export type EmbroiderySelectorProps = {
	legend?: string;
	namePrefix: string;
	option: EmbroideryOptionCoreFragment;
	defaultConfiguration?: EmbroideryConfiguration | null;
} & React.ComponentProps<'div'>;

export const EmbroideryOptionSelector: FC<EmbroiderySelectorProps> = ({
	legend,
	option,
	namePrefix,
	defaultConfiguration,
	className,
	...rest
}) => {
	return (
		<div
			className={cn(
				'flex flex-col items-stretch gap-2 rounded-sm border-2 border-neutral-600 bg-neutral-800/50 leading-0 font-semibold select-none',
				'min-h-12 px-3 text-base lg:min-h-14 lg:px-4 lg:text-lg',
				'has-[*>:checked]:border-primary has-[*>:checked]:outline-2 has-[*>:checked]:-outline-offset-4 has-[*>:checked]:outline-neutral-800',
				className,
			)}
			{...rest}
		>
			<Checkbox
				value="enabled"
				name={namePrefix}
				defaultChecked={!!defaultConfiguration}
				labelProps={{
					className: 'flex h-12 lg:h-14',
				}}
			>
				{legend}{' '}
				{option.items && (
					<>
						{' '}
						(+ <FormattedItemPrice items={option.items} />)
					</>
				)}
			</Checkbox>
			{defaultConfiguration && (
				<div className="mt-2 flex flex-col gap-4 pb-4">
					{option.embroideryCollections?.map((collection) => (
						<fieldset key={collection._id}>
							<Paragraph className="mb-2 font-semibold" asChild>
								<legend className="">{collection.name}</legend>
							</Paragraph>
							<div className="flex flex-wrap gap-2">
								{collection.embroideries?.map((embroidery) => {
									const image = imageFromSanityImage(embroidery.image);
									if (!image) {
										return null;
									}

									return (
										<RadioTileFrame
											key={embroidery._key}
											name={`${namePrefix}[embroideryId]`}
											value={`${collection._id}:${embroidery._key}`}
											defaultChecked={
												collection._id ===
													defaultConfiguration?.collectionId &&
												embroidery._key ===
													defaultConfiguration?.embroideryKey
											}
											labelProps={{
												className: 'p-2 bg-preview-background ',
											}}
										>
											<ImageBlock
												{...image}
												alt={embroidery.name || ''}
												className="object-contain"
												style={{width: 72, height: 72}}
											/>
										</RadioTileFrame>
									);
								})}
							</div>
						</fieldset>
					))}
				</div>
			)}
		</div>
	);
};
